import React from "react";
import {
  Box,
  Typography,
  Paper,
  Avatar,
  Chip,
  Divider,
  Icon,
  alpha,
  useTheme,
  Skeleton,
} from "@mui/material";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { GRNActivityLog } from "../../types/grn.types";

dayjs.extend(relativeTime);

// Helper function to format timestamps
const formatTimestamp = (timestamp: number) => {
  return dayjs(timestamp).format("MMM DD, YYYY, hh:mm A");
};

// Activity configuration for GRN-specific icons, colors, and labels
const grnActivityConfig: Record<
  string,
  {
    icon: string;
    color: string;
    bgColor: string;
    label: string;
    description: string;
  }
> = {
  created: {
    icon: "add_circle",
    color: "#3B82F6",
    bgColor: "#EFF6FF",
    label: "Created",
    description: "GRN was created",
  },
  updated: {
    icon: "edit",
    color: "#0891B2",
    bgColor: "#ECFEFF",
    label: "Updated",
    description: "GRN details were modified",
  },
  submitted: {
    icon: "send",
    color: "#7C3AED",
    bgColor: "#F3E8FF",
    label: "Submitted",
    description: "GRN submitted for approval",
  },
  approved: {
    icon: "check_circle",
    color: "#059669",
    bgColor: "#ECFDF5",
    label: "Approved",
    description: "GRN approved and inventory updated",
  },
  rejected: {
    icon: "cancel",
    color: "#DC2626",
    bgColor: "#FEF2F2",
    label: "Rejected",
    description: "GRN was rejected",
  },
  inventory_created: {
    icon: "inventory",
    color: "#059669",
    bgColor: "#ECFDF5",
    label: "Inventory Updated",
    description: "Stock entries created in inventory",
  },
  po_closed: {
    icon: "task_alt",
    color: "#6B7280",
    bgColor: "#F9FAFB",
    label: "PO Closed",
    description: "Purchase Order marked as closed",
  },
  batch_created: {
    icon: "label",
    color: "#F59E0B",
    bgColor: "#FFFBEB",
    label: "Batch Created",
    description: "New inventory batch created",
  },
  quality_check: {
    icon: "verified",
    color: "#10B981",
    bgColor: "#ECFDF5",
    label: "Quality Check",
    description: "Item quality assessment completed",
  },
  warehouse_assigned: {
    icon: "warehouse",
    color: "#6366F1",
    bgColor: "#EEF2FF",
    label: "Warehouse Assigned",
    description: "Items assigned to warehouse location",
  },
};

interface GRNActivityTimelineProps {
  activities: GRNActivityLog[];
  loading?: boolean;
}

// Skeleton component for loading state
const GRNActivityTimelineSkeleton: React.FC = () => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
    {[1, 2, 3].map((item) => (
      <Box key={item} sx={{ display: "flex", gap: 2 }}>
        <Skeleton variant="circular" width={40} height={40} />
        <Box sx={{ flex: 1 }}>
          <Skeleton
            variant="rectangular"
            height={100}
            sx={{ borderRadius: 2 }}
          />
        </Box>
      </Box>
    ))}
  </Box>
);

export const GRNActivityTimeline: React.FC<GRNActivityTimelineProps> = ({
  activities,
  loading = false,
}) => {
  const theme = useTheme();

  if (loading) {
    return <GRNActivityTimelineSkeleton />;
  }

  if (!activities || activities.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          py: 6,
          textAlign: "center",
        }}
      >
        <Icon
          sx={{
            fontSize: 48,
            color: "text.secondary",
            mb: 2,
          }}
        >
          history
        </Icon>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
          No Activity Yet
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ maxWidth: 300 }}
        >
          GRN activity will appear here as actions are performed on this goods
          receipt note.
        </Typography>
      </Box>
    );
  }

  const sortedActivities = [...activities].sort(
    (a, b) => b.performed_at - a.performed_at
  );

  return (
    <Box>
      {sortedActivities.map((activity, index) => {
        const config = grnActivityConfig[activity.action] || {
          icon: "info",
          color: theme.palette.primary.main,
          bgColor: alpha(theme.palette.primary.main, 0.1),
          label: activity.action,
          description: activity.description || "",
        };

        const isLast = index === activities.length - 1;

        return (
          <Box
            key={activity._id}
            sx={{ position: "relative", pb: isLast ? 0 : 3 }}
          >
            {/* Timeline line */}
            {!isLast && (
              <Box
                sx={{
                  position: "absolute",
                  left: 20,
                  top: 48,
                  bottom: 0,
                  width: 2,
                  bgcolor: "#E5E7EB",
                  zIndex: 0,
                }}
              />
            )}

            <Box
              sx={{ display: "flex", gap: 2, position: "relative", zIndex: 1 }}
            >
              {/* Timeline icon */}
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  bgcolor: config.bgColor,
                  color: config.color,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  border: `2px solid ${config.color}`,
                  flexShrink: 0,
                }}
              >
                <Icon sx={{ fontSize: 20 }}>{config.icon}</Icon>
              </Box>

              {/* Activity content */}
              <Paper
                variant="outlined"
                sx={{
                  flex: 1,
                  p: 2.5,
                  borderRadius: 2,
                  bgcolor: "background.paper",
                  border: `1px solid ${alpha(config.color, 0.2)}`,
                  "&:hover": {
                    boxShadow: 1,
                    borderColor: alpha(config.color, 0.3),
                  },
                  transition: "all 0.2s ease-in-out",
                }}
              >
                {/* Header */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 1.5,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Chip
                      label={config.label}
                      size="small"
                      sx={{
                        bgcolor: config.bgColor,
                        color: config.color,
                        fontWeight: 600,
                        border: `1px solid ${alpha(config.color, 0.3)}`,
                      }}
                    />
                  </Box>

                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      fontWeight: 500,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {formatTimestamp(activity.performed_at)}
                  </Typography>
                </Box>

                {/* Description */}
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.primary",
                    mb: activity.details ? 1 : 0,
                    lineHeight: 1.5,
                  }}
                >
                  {activity.description || config.description}
                </Typography>

                {/* Additional details */}
                {activity.details && (
                  <>
                    <Divider sx={{ my: 1.5 }} />
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 600, mb: 1, display: "block" }}
                      >
                        Additional Details:
                      </Typography>
                      <Box
                        sx={{
                          bgcolor: alpha("#000", 0.02),
                          p: 1.5,
                          borderRadius: 1,
                          border: `1px solid ${alpha("#000", 0.08)}`,
                        }}
                      >
                        {typeof activity.details === "object" ? (
                          Object.entries(activity.details).map(
                            ([key, value]) => (
                              <Box
                                key={key}
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  py: 0.5,
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ textTransform: "capitalize" }}
                                >
                                  {key.replace(/_/g, " ")}:
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.primary"
                                >
                                  {String(value)}
                                </Typography>
                              </Box>
                            )
                          )
                        ) : (
                          <Typography variant="caption" color="text.primary">
                            {String(activity.details)}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </>
                )}

                {/* User information */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mt: 1.5,
                    pt: 1.5,
                    borderTop: `1px solid ${alpha("#000", 0.08)}`,
                  }}
                >
                  <Icon sx={{ fontSize: 16, color: "text.secondary" }}>
                    person
                  </Icon>
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography variant="caption" color="text.secondary">
                      {activity.performed_by_name || "Unknown User"}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: "0.65rem", opacity: 0.8 }}
                    >
                      {dayjs(activity.performed_at).fromNow()}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};
